import React from 'react';

/**
 * DynamicStageRenderer - Renders form fields based on WorkflowConfig stage definition.
 *
 * Stage types:
 *   - questionnaire: Shows control table with configurable columns
 *   - form: Shows a form with configurable fields
 *   - review: Shows review view with accept/reject actions
 *   - completed: Shows report link
 *
 * Each field is rendered based on its type from the field definitions.
 */

// Field type to input renderer
function FieldInput({ fieldDef, value, onChange, onBlur, readOnly }) {
    const type = fieldDef?.type || 'text';
    const commonStyle = {
        width: '100%', background: readOnly ? 'var(--ap-surface-2)' : 'var(--ap-surface)',
        border: '1px solid var(--ap-border)', borderRadius: 4, color: 'var(--ap-text)',
        fontSize: 12, padding: '7px 12px', fontFamily: 'var(--ap-font)',
        boxSizing: 'border-box', opacity: readOnly ? 0.7 : 1,
    };

    if (readOnly) {
        if (type === 'select' && fieldDef.options) {
            const opt = fieldDef.options.find(o => o.value === value);
            return <span style={{ fontSize: 11, color: 'var(--ap-text-2)' }}>{opt?.label || value || '-'}</span>;
        }
        return <span style={{ fontSize: 11, color: 'var(--ap-text-2)' }}>{value || '-'}</span>;
    }

    switch (type) {
        case 'textarea':
            return (
                <textarea style={{ ...commonStyle, resize: 'vertical', minHeight: 60, lineHeight: 1.5 }}
                    value={value || ''} placeholder={fieldDef.placeholder || fieldDef.label}
                    onChange={e => onChange(e.target.value)} onBlur={onBlur} />
            );
        case 'select':
            return (
                <select style={{ ...commonStyle, cursor: 'pointer' }}
                    value={value || ''} onChange={e => { onChange(e.target.value); if (onBlur) onBlur(); }}>
                    <option value="">-- Select --</option>
                    {(fieldDef.options || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            );
        case 'date':
            return (
                <input type="date" style={commonStyle}
                    value={value || ''} onChange={e => onChange(e.target.value)} onBlur={onBlur} />
            );
        case 'number':
            return (
                <input type="number" style={commonStyle}
                    value={value || ''} placeholder={fieldDef.placeholder || '0'}
                    onChange={e => onChange(e.target.value)} onBlur={onBlur} />
            );
        case 'time':
            // Time field renders as Hour + Mins side by side
            const hours = value?.hours || 0;
            const mins = value?.mins || 0;
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, color: 'var(--ap-text-3)' }}>Hr</span>
                    <input type="number" min="0" style={{ ...commonStyle, width: 50, textAlign: 'center', padding: '4px 6px' }}
                        value={hours} onChange={e => onChange({ hours: e.target.value, mins })} />
                    <span style={{ fontSize: 10, color: 'var(--ap-text-3)' }}>Min</span>
                    <input type="number" min="0" max="59" style={{ ...commonStyle, width: 50, textAlign: 'center', padding: '4px 6px' }}
                        value={mins} onChange={e => onChange({ hours, mins: e.target.value })} />
                </div>
            );
        case 'file':
            return (
                <label style={{ cursor: 'pointer', fontSize: 11, color: 'var(--ap-accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {value ? `${value} file(s)` : '+ Upload'}
                    <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
                        onChange={e => { if (e.target.files[0] && onChange) onChange(e.target.files[0]); }} />
                </label>
            );
        case 'checkbox':
            return (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
                    <span style={{ fontSize: 11, color: 'var(--ap-text-2)' }}>{fieldDef.label}</span>
                </label>
            );
        default: // text
            return (
                <input type="text" style={commonStyle}
                    value={value || ''} placeholder={fieldDef.placeholder || fieldDef.label}
                    onChange={e => onChange(e.target.value)} onBlur={onBlur} />
            );
    }
}

/**
 * FormStageRenderer - Renders a form with fields from the stage config.
 * Used for stage_type: 'form'
 */
export function FormStageRenderer({ stageConfig, fieldDefs, formData, setFormData, onTransition, transitionLabel, onBack, onError }) {
    const stageFields = (stageConfig?.fields || []).map(fId => fieldDefs.find(f => f.id === fId)).filter(Boolean);

    const handleTransition = () => {
        const missing = stageFields.filter(f => f.required && (!formData[f.id] || formData[f.id] === ''));
        if (missing.length > 0) {
            const msg = `"${missing[0].label}" is required.`;
            if (onError) onError(msg);
            else alert(msg);
            return;
        }
        if (onTransition) onTransition();
    };

    // Group time fields together
    const timeFields = stageFields.filter(f => f.type === 'time');
    const otherFields = stageFields.filter(f => f.type !== 'time');

    return (
        <div style={{ marginTop: 16, background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 'var(--ap-r-lg)', padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ap-text)', marginBottom: 4 }}>{stageConfig.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ap-text-3)', marginBottom: 16 }}>{stageConfig.description}</div>

            {/* Regular fields in grid */}
            <div style={{ display: 'grid', gridTemplateColumns: otherFields.length > 2 ? '1fr 1fr 1fr' : '1fr 1fr', gap: 10, marginBottom: 14 }}>
                {otherFields.map(fieldDef => (
                    <div key={fieldDef.id}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ap-text-3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            {fieldDef.label} {fieldDef.required && '*'}
                        </div>
                        <FieldInput
                            fieldDef={fieldDef}
                            value={formData[fieldDef.id] || ''}
                            onChange={val => setFormData({ ...formData, [fieldDef.id]: val })}
                        />
                    </div>
                ))}
            </div>

            {/* Time fields in their own section */}
            {timeFields.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ap-text-3)', marginBottom: 6, textTransform: 'uppercase' }}>Time Spent</div>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(timeFields.length, 3)}, 1fr)`, gap: 8, marginBottom: 8 }}>
                        {timeFields.map(fieldDef => (
                            <div key={fieldDef.id} style={{ background: 'var(--ap-surface-2)', borderRadius: 'var(--ap-r)', padding: 8, border: '1px solid var(--ap-border)' }}>
                                <div style={{ fontSize: 9, color: 'var(--ap-text-3)', fontWeight: 600, marginBottom: 4 }}>{fieldDef.label}</div>
                                <FieldInput
                                    fieldDef={fieldDef}
                                    value={formData[fieldDef.id] || { hours: 0, mins: 0 }}
                                    onChange={val => setFormData({ ...formData, [fieldDef.id]: val })}
                                />
                            </div>
                        ))}
                    </div>
                    {/* Auto-calculated total */}
                    {timeFields.length > 1 && (() => {
                        const totalMins = timeFields.reduce((sum, f) => {
                            const v = formData[f.id] || {};
                            return sum + (parseInt(v.hours) || 0) * 60 + (parseInt(v.mins) || 0);
                        }, 0);
                        return (
                            <div style={{ background: 'var(--ap-surface-2)', borderRadius: 'var(--ap-r)', padding: 8, border: '1px solid var(--ap-border)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 9, color: 'var(--ap-text-3)', fontWeight: 600 }}>Total:</span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ap-accent)' }}>{Math.floor(totalMins / 60)}h {totalMins % 60}m</span>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Transition button */}
            {(transitionLabel || onBack) && (
                <div style={{ display: 'flex', justifyContent: onBack ? 'space-between' : 'flex-end', paddingTop: 12, borderTop: '1px solid var(--ap-border)' }}>
                    {onBack && (
                        <button className="ap-btn ap-btn--ghost" style={{ padding: '8px 24px', fontSize: 13 }} onClick={onBack}>
                            &#8592; Back
                        </button>
                    )}
                    {transitionLabel && (
                        <button className="ap-btn ap-btn--primary" style={{ padding: '8px 24px', fontSize: 13 }} onClick={handleTransition}>
                            {transitionLabel}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * TableColumnRenderer - Renders a table column (header + cell) for a field.
 * Used inside questionnaire tables.
 */
export function TableHeader({ fieldDef, style }) {
    return (
        <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: 'var(--ap-text-3)', textTransform: 'uppercase', borderBottom: '1px solid var(--ap-border)', minWidth: fieldDef.type === 'textarea' ? 140 : fieldDef.type === 'select' ? 90 : 100, ...style }}>
            {fieldDef.label}
        </th>
    );
}

export function TableCell({ fieldDef, value, onChange, onBlur, readOnly }) {
    return (
        <td style={{ padding: '4px 6px' }}>
            <FieldInput fieldDef={fieldDef} value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} />
        </td>
    );
}

export { FieldInput };
export default FormStageRenderer;
